import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { apiService, LicenseStatus } from '../../services/apiService';
import { licenceAdminEnabled } from '../../utils/licensing';

/**
 * Licence warning/blocked banner for client Docker containers. The status
 * endpoint only exists in licence-enforced containers, so elsewhere the
 * request 404s and nothing renders.
 */

const REASON_TEXT: Record<string, string> = {
  missing_license_key: 'No licence key is configured (LICENSE_KEY).',
  unknown_key: 'The licence key is not recognised.',
  revoked: 'This licence has been revoked.',
  expired: 'This licence has expired.',
  install_mismatch: 'This licence key is already in use by another installation.',
  lease_lapsed: 'The licence server has not been reachable for over 30 days.',
  server_unreachable: 'The licence server could not be reached to activate this installation.',
  clock_rollback: 'The system clock appears to have been changed.',
  invalid_signature: 'The licence could not be verified.',
  database_unavailable: 'The licence could not be checked because the database is unavailable.',
  no_lease: 'This installation has not been activated.',
};

function formatDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
}

export default function LicenceBanner() {
  const [status, setStatus] = useState<LicenseStatus | null>(null);

  useEffect(() => {
    if (licenceAdminEnabled) return; // FenxLabs-hosted portal — not a licensed container
    let cancelled = false;
    const load = () => apiService.getLicenseStatus()
      .then(s => { if (!cancelled) setStatus(s); })
      .catch(() => { if (!cancelled) setStatus(null); });
    load();
    const timer = setInterval(load, 10 * 60 * 1000);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  if (!status?.enforced) return null;

  if (!status.valid) {
    return (
      <div className="pk-note pk-note-danger" style={{ marginBottom: 20 }}>
        <AlertTriangle size={18} />
        <div>
          <p style={{ fontWeight: 700, margin: 0 }}>API requests are disabled — this installation is not licensed</p>
          <p className="pk-small" style={{ marginTop: 3 }}>
            {REASON_TEXT[status.reason || ''] || `Reason: ${status.reason}`} Contact FenxLabs to renew.
            {status.installId && <> Install ID: <span className="pk-mono">{status.installId}</span></>}
          </p>
        </div>
      </div>
    );
  }

  if (status.warning === 'licence_expiring') {
    return (
      <div className="pk-note pk-note-warning" style={{ marginBottom: 20 }}>
        <AlertTriangle size={18} />
        <p className="pk-small" style={{ margin: 0 }}>
          Your licence expires on <strong>{formatDate(status.licenceExpiresAt)}</strong>. Contact FenxLabs to renew.
        </p>
      </div>
    );
  }

  if (status.warning === 'checkin_overdue') {
    return (
      <div className="pk-note pk-note-warning" style={{ marginBottom: 20 }}>
        <AlertTriangle size={18} />
        <p className="pk-small" style={{ margin: 0 }}>
          The licence server can't be reached. API requests will stop on <strong>{formatDate(status.leaseValidUntil)}</strong> unless
          outbound HTTPS to the licence server is restored.
        </p>
      </div>
    );
  }

  return null;
}
